// Copyright: IQGeo Limited 2010-2022
import $ from 'jquery';
import myw from 'myWorld/base/core';
import '../baseComponent';

/**
 * @typedef {Object} FormRow
 * @property {String} label
 * @property {Array<View|String>} component
 */

/**
 * UI component managing a set of {@link FormComponent}s.
 *
 * Displays its components as a table, each with an optional label. Also has
 * areas for adding action buttons at the bottom of the form. Provides
 * an API for getting and setting control data by name and for listening for
 * data changes. Also provides support for message translation (localization).
 *
 * @class  Form
 * @param  {Array<FormRow>} options.rows                  Form rows for rendering.
 * @param  {Array<View|String>} options.bottomLeft    Component(s) to render bottom left
 * @param  {Array<View|String>} options.bottomRight   Component(s) to render bottom right
 * @param  {function} options.onChange                    Callback function for field changes
 * @param  {String} options.messageGroup                  Default group to use for localisation messages.
 *
 * @example
 *
 * new myw.Form({
 *   rows: [
 *     {
 *       label: "{:from_label}",
 *       components: [
 *         new myw.Input(),
 *         "<span>m</span>",
 *       ]
 *     }
 *   ],
 *   bottomLeft: new myw.Button({
 *     text: 'Cancel'
 *   }),
 *   bottomRight: new myw.Button({
 *     text: 'Save'
 *   },
 *   messageGroup: "TracePlugin"
 * });
 *
 * @extends {BaseComponent}
 */
myw.Form = myw.BaseComponent.extend({
    className: 'ui-form',
    initialize(options) {
        this.options = options; //ENH: unnecessary?
        this.formInputs = {};

        if (options.messageGroup) {
            this.messageGroup = options.messageGroup;
        }

        myw.View.prototype.initialize.call(this, options);
        this.render();
    },

    /**
     * Returns all form components value
     * @return {object}
     */
    getValues() {
        const values = {};

        Object.keys(this.formInputs).map(key => {
            this._bindFormValue(values, this.formInputs[key]);
        });

        return values;
    },

    setValues(values) {
        Object.keys(values).forEach(key => {
            try {
                this.setValue(key, values[key]);
            } catch (e) {
                //ignore missing keys
            }
        });
    },

    /**
     * Set a value of a named input
     *  @param name Field name
     *  @param value field value
     */
    setValue(name, value) {
        this.formInputs[name].setValue(value);
    },

    /**
     * Get a value of a named input
     *  @param name Field name
     *  @return {mixed} Field value
     */
    getValue(name) {
        return this.formInputs[name].getValue();
    },

    /**
     * Get a field
     *  @param name Field name
     *  @return {FormComponent} Field component
     */
    getField(name) {
        return this.formInputs[name];
    },

    _bindFormValue(valueMap, component) {
        const name = component.getName();

        if (name) {
            valueMap[name] = component.getValue();
        }
    },

    render() {
        this.$el.empty();
        const table = $('<table>');
        this.$el.append(table);
        this._renderRows(table, this.options.rows || []);
        this._renderButtonContainer(this.options.bottomLeft, 'left-section');
        this._renderButtonContainer(this.options.bottomRight, 'right-section');
        myw.translate(this.messageGroup, this.$el);
    },

    _renderButtonContainer(buttonDD, cssClass) {
        let buttons = buttonDD;
        if (!buttons) return;

        if (!Array.isArray(buttons)) {
            buttons = [buttons];
        }

        const buttonContainer = $('<div>', { class: cssClass });
        this._renderComponents(buttonContainer, buttons);
        this.$el.append(buttonContainer);
    },

    _renderRows(table, rows) {
        rows.map(this._renderRow.bind(this, table));
    },

    _renderRow(table, rowDD) {
        const tr = $('<tr class="ui-form-row">');
        const tdLabel = $(rowDD.labelObj || '<td class="ui-label">');
        const tdComponents = $(rowDD.componentsObj || '<td class="ui-form-component-wrapper">');

        if (rowDD.label) {
            tdLabel.append(rowDD.label);
            tr.append(tdLabel);
        }
        this._renderComponents(tdComponents, rowDD.components || []);
        tr.append(tdComponents);
        table.append(tr);
    },

    _renderComponents(el, components) {
        components.map(this._renderComponent.bind(this, el));
    },

    _renderComponent(el, component) {
        if (typeof component.render == 'function') {
            component.render({ parent: el });
            this._addFormInput(component);
            return;
        }

        el.append(component.toString());
    },

    _addFormInput(component) {
        if (
            typeof component.hasComponent === 'function' &&
            typeof component.getComponent === 'function'
        ) {
            if (component.hasComponent()) {
                this._addFormInput(component.getComponent());
            }
        }

        if (typeof component.getValue === 'function' && typeof component.getName === 'function') {
            //Clone any on change callbacks, and register our own for tracking changes across the form.
            const implementation = component.options.onChange || new Function();

            component.options.onChange = () => {
                implementation.call({}, component);
                this._onChange.call(this, component);
            };

            this.formInputs[component.getName()] = component;
        }
    },

    _onChange(component) {
        if (this.options.onChange) {
            this.options.onChange(component.getName(), component.getValue(), this);
        }
    }
});

export default myw.Form;
